import type { ReactNode, Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';

export type LegendSwatchKind = 'box' | 'line' | 'dash';

export interface LegendSwatchProps {
  /** Concrete color (charts pick mode-aware colors, so this is a hex/rgb string). */
  color: string;
  label: string;
  /** `box` = filled square, `line` = solid rule, `dash` = dashed rule. */
  kind?: LegendSwatchKind;
}

/** Static legend key used by the chart cards (non-interactive). */
export function LegendSwatch({ color, label, kind = 'box' }: LegendSwatchProps) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
      }}
    >
      {kind === 'box' && (
        <Box component="span" sx={{ width: 10, height: 10, bgcolor: color, borderRadius: '2px' }} />
      )}
      {kind === 'line' && (
        <Box
          component="span"
          sx={{ width: 14, height: '2px', bgcolor: color, borderRadius: '1px' }}
        />
      )}
      {kind === 'dash' && (
        <Box component="span" sx={{ width: 14, height: 0, borderTop: `2px dashed ${color}` }} />
      )}
      {label}
    </Box>
  );
}

export interface ChartCardProps {
  /** Card title. */
  title: string;
  /** Legend / toggle slot rendered top-right of the header. */
  legend?: ReactNode;
  /** The chart (SVG) or an empty/loading placeholder. */
  children: ReactNode;
  /** Fixed height of the plot area in px (matches the prototype's 250–280). */
  height?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Titled chart container with a legend slot — the shell every dashboard/report
 * chart sits in. Port of the prototype `ChartCard`: outlined paper card, a
 * header row (title left, legend right), and a fixed-height plot area. The
 * chart itself is supplied as children so the same card wraps the area chart,
 * the stacked-bar chart, and empty/loading states.
 */
export function ChartCard({ title, legend, children, height = 280, ref }: ChartCardProps) {
  return (
    <Card
      ref={ref}
      variant="outlined"
      sx={{ p: '20px 22px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        {legend && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {legend}
          </Box>
        )}
      </Box>
      <Box sx={{ height, minWidth: 0 }}>{children}</Box>
    </Card>
  );
}
